import { fetchWithAuth, BASE_URL } from "../api/client";

/**
 * Leads Management Service
 * Handles all API calls for leads from RapidRates and other sources
 */

// Valid lead statuses (lowercase for display, uppercase for API)
export const LEAD_STATUSES = {
  NEW: "new",
  CONTACTED: "contacted",
  PROCESSING: "processing",
  FOLLOW_UP: "follow_up",
  QUALIFIED: "qualified",
  CONVERTED: "converted",
  LOST: "lost",
};

// Valid stage transitions - defines which statuses can transition to which
// Backend enforces these rules, so UI should only show valid options
export const VALID_TRANSITIONS = {
  new: ["contacted", "processing", "follow_up", "qualified", "lost"],
  contacted: ["new", "processing", "follow_up", "qualified", "lost"],
  processing: ["new", "contacted", "follow_up", "qualified", "lost"],
  follow_up: ["new", "contacted", "processing", "qualified", "lost"],
  qualified: ["new", "contacted", "processing", "follow_up", "converted", "lost"],
  converted: [], // Terminal state - deal closed
  lost: ["new", "contacted", "processing", "follow_up", "qualified"], // Can be revived
};

/**
 * Get valid status transitions for a given current status
 * @param {string} currentStatus - The current status of the lead
 * @returns {string[]} Array of valid status values the lead can transition to
 */
export const getValidTransitions = (currentStatus) => {
  const normalized = (currentStatus || "new").toLowerCase();
  return VALID_TRANSITIONS[normalized] || [];
};

// Source display configuration for lead origins
export const SOURCE_CONFIG = {
  rapidrates: {
    label: "RapidRates.io",
    color: "blue",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-400",
  },
  nhamy: {
    label: "Nhamy.us",
    color: "green",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
  },
  cloudrealtor: {
    label: "CloudRealtor.io",
    color: "purple",
    bgClass: "bg-purple-100 dark:bg-purple-900/30",
    textClass: "text-purple-700 dark:text-purple-400",
  },
  web: {
    label: "Web",
    color: "cyan",
    bgClass: "bg-cyan-100 dark:bg-cyan-900/30",
    textClass: "text-cyan-700 dark:text-cyan-400",
  },
  unknown: {
    label: "Unknown",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
  },
};

// Status display configuration
export const STATUS_CONFIG = {
  new: {
    label: "New",
    color: "sky",
    bgClass: "bg-sky-100 dark:bg-sky-900/30",
    textClass: "text-sky-700 dark:text-sky-400",
    borderClass: "border-sky-200 dark:border-sky-800",
  },
  contacted: {
    label: "Contacted",
    color: "amber",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-400",
    borderClass: "border-amber-200 dark:border-amber-800",
  },
  processing: {
    label: "Processing",
    color: "blue",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-400",
    borderClass: "border-blue-200 dark:border-blue-800",
  },
  follow_up: {
    label: "Follow-Up",
    color: "orange",
    bgClass: "bg-orange-100 dark:bg-orange-900/30",
    textClass: "text-orange-700 dark:text-orange-400",
    borderClass: "border-orange-200 dark:border-orange-800",
  },
  qualified: {
    label: "Qualified",
    color: "violet",
    bgClass: "bg-violet-100 dark:bg-violet-900/30",
    textClass: "text-violet-700 dark:text-violet-400",
    borderClass: "border-violet-200 dark:border-violet-800",
  },
  converted: {
    label: "Converted",
    color: "emerald",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/30",
    textClass: "text-emerald-700 dark:text-emerald-400",
    borderClass: "border-emerald-200 dark:border-emerald-800",
  },
  lost: {
    label: "Lost",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-600 dark:text-slate-400",
    borderClass: "border-slate-200 dark:border-slate-700",
  },
  archive: {
    label: "Archived",
    color: "slate",
    bgClass: "bg-slate-100 dark:bg-slate-800",
    textClass: "text-slate-500 dark:text-slate-500",
    borderClass: "border-slate-200 dark:border-slate-700",
  },
};

/**
 * Fetch leads with optional filters
 * @param {Object} filters - Optional filters
 * @param {string} filters.source - Filter by source (e.g., "rapidrates")
 * @param {string} filters.status - Filter by status
 * @param {string} filters.assigned_to - Filter by assigned loan officer
 * @param {string} filters.team_id - Filter by team (returns leads accessible to any team member)
 * @param {boolean} filters.include_team - If true, include leads from user's team
 * @param {number} filters.limit - Max results to return
 * @returns {Promise<{leads: Lead[], total_count: number}>}
 */
export const fetchLeads = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.source) params.append("source", filters.source);
  if (filters.status) params.append("status", filters.status);
  if (filters.assigned_to) params.append("assigned_to", filters.assigned_to);
  if (filters.team_id) params.append("team_id", filters.team_id);
  if (filters.include_team) params.append("include_team", "true");
  if (filters.limit) params.append("limit", filters.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/leads${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch leads");
  }

  return response.json();
};

/**
 * Fetch lead statistics for dashboard cards
 * @param {Object} options - Optional settings
 * @param {boolean} options.include_team - If true, include stats from user's team
 * @returns {Promise<{new: number, contacted: number, qualified: number, converted: number, lost: number, total: number}>}
 */
export const fetchLeadStats = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.include_team) params.append("include_team", "true");

  const queryString = params.toString();
  const url = `${BASE_URL}/api/leads/stats${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch lead stats");
  }

  return response.json();
};

/**
 * Fetch a single lead by ID
 * @param {string} leadId
 * @returns {Promise<Lead>}
 */
export const fetchLead = async (leadId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Lead not found");
    }
    throw new Error("Failed to fetch lead");
  }

  return response.json();
};

/**
 * Create a new lead
 * @param {Object} leadData - The lead data
 * @returns {Promise<Lead>}
 */
export const createLead = async (leadData) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(leadData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create lead");
  }

  return response.json();
};

/**
 * Update lead status
 * @param {string} leadId
 * @param {string} status - One of: new, contacted, qualified, converted, lost
 * @param {string} note - Optional note about the status change
 * @returns {Promise<Lead>}
 */
export const updateLeadStatus = async (leadId, status, note = "") => {
  const url = `${BASE_URL}/api/leads/${leadId}/status`;
  // Backend expects uppercase status values (e.g., 'FOLLOW_UP' not 'follow_up')
  const apiStatus = status.toUpperCase();
  const body = { status: apiStatus, note };

  console.log('[updateLeadStatus] Request:', { url, leadId, status, apiStatus, note, body });

  const response = await fetchWithAuth(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[updateLeadStatus] Error response:', { status: response.status, errorData });
    throw new Error(errorData.detail || errorData.message || "Failed to update lead status");
  }

  return response.json();
};

/**
 * Assign lead to a loan officer
 * @param {string} leadId
 * @param {string} assignedTo - Loan officer email
 * @param {boolean} sendNotification - Whether to send email notification
 * @returns {Promise<Lead>}
 */
export const assignLead = async (leadId, assignedTo, sendNotification = false) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/assign`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      assigned_to: assignedTo,
      send_notification: sendNotification
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to assign lead");
  }

  return response.json();
};

/**
 * Add a follow-up note to a lead
 * @param {string} leadId
 * @param {string} note
 * @param {Array<{email: string, name: string}>} mentionedUsers - Users mentioned in the note (will receive email notification)
 * @returns {Promise<Lead>}
 */
export const addLeadNote = async (leadId, note, mentionedUsers = []) => {
  // Validate note is not empty (prevents 422 validation errors from backend)
  const trimmedNote = note?.trim();
  if (!trimmedNote) {
    throw new Error("Note cannot be empty");
  }

  // Check that note has actual content beyond just @mentions
  const contentWithoutMentions = trimmedNote.replace(/@\w+(\.\w+)*(@[\w.]+)?/g, '').trim();
  if (!contentWithoutMentions && mentionedUsers.length > 0) {
    throw new Error("Please add a message along with the mention");
  }

  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      note: trimmedNote,
      mentioned_users: mentionedUsers.length > 0
        ? mentionedUsers.map(u => ({ email: u.email, name: u.name || null }))
        : null,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to add note");
  }

  return response.json();
};

/**
 * Update an existing note on a lead
 * @param {string} leadId
 * @param {number} noteIndex - 0-based index of the note to update
 * @param {string} note - New note content
 * @returns {Promise<Lead>}
 */
export const updateLeadNote = async (leadId, noteIndex, note) => {
  // Validate note is not empty (prevents 422 validation errors from backend)
  const trimmedNote = note?.trim();
  if (!trimmedNote) {
    throw new Error("Note cannot be empty");
  }

  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/notes/${noteIndex}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ note: trimmedNote }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("You can only edit your own notes");
    }
    throw new Error("Failed to update note");
  }

  return response.json();
};

/**
 * Delete a note from a lead
 * @param {string} leadId
 * @param {number} noteIndex - 0-based index of the note to delete
 * @returns {Promise<Lead>}
 */
export const deleteLeadNote = async (leadId, noteIndex) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/notes/${noteIndex}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("You can only delete your own notes");
    }
    throw new Error("Failed to delete note");
  }

  return response.json();
};

/**
 * Toggle a reaction (emoji) on a lead note
 * @param {string} leadId
 * @param {number} noteIndex - 0-based index of the note
 * @param {string} emoji - The emoji to toggle
 * @returns {Promise<Lead>}
 */
export const toggleNoteReaction = async (leadId, noteIndex, emoji) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/notes/${noteIndex}/reactions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ emoji }),
  });

  if (!response.ok) {
    throw new Error("Failed to toggle reaction");
  }

  return response.json();
};

/**
 * Redeem an access code to gain access to a lead
 * This is a one-time operation - after successful redemption, the user has permanent access
 * The backend matches the access code to the correct lead internally
 * @param {string} accessCode - The 8-character alphanumeric access code
 * @returns {Promise<{success: boolean, lead: Lead}>}
 */
export const redeemAccessCode = async (accessCode) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/redeem`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ access_code: accessCode }),
    // Prevent redirect to login on errors - handle errors locally in the UI
    skipGlobalAuthHandler: true,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 401) {
      throw new Error("Please log in to redeem this access code");
    }
    if (response.status === 404) {
      throw new Error("Invalid or expired access code");
    }
    if (response.status === 409) {
      throw new Error("Access code has already been redeemed");
    }
    throw new Error(errorData.message || "Failed to redeem access code");
  }

  return response.json();
};

/**
 * Check if the current user has access to a lead's full details
 * @param {Object} lead - The lead object
 * @param {string} userEmail - Current user's email
 * @returns {boolean}
 */
export const hasLeadAccess = (lead, userEmail) => {
  if (!lead) return false;

  // If lead is already activated/unlocked (has contact info visible), grant access
  if (lead.is_activated === true) return true;

  // If lead has contact name populated, it's been revealed
  if (lead.contact_name && lead.contact_name !== "Contact Hidden") return true;

  // If lead status indicates it's been worked on (not new), grant access
  // Someone must have seen the contact info to contact/qualify them
  if (lead.status && lead.status !== "new") return true;

  // Check if user is in accessible_by_emails array (set by backend on code redemption)
  if (userEmail) {
    const email = userEmail.toLowerCase();
    const accessibleEmails = lead.accessible_by_emails || [];
    if (accessibleEmails.some(e => e.toLowerCase() === email)) return true;
  }

  // Check if assigned to this user
  if (userEmail && lead.assigned_to && lead.assigned_to.toLowerCase() === userEmail.toLowerCase()) {
    return true;
  }

  return false;
};

/**
 * Convert a lead to a mortgage case
 * @param {string} leadId
 * @param {Object} caseData - Optional case data overrides
 * @param {string} caseData.borrower_name
 * @param {string} caseData.borrower_email
 * @param {string} caseData.property_address
 * @returns {Promise<{success: boolean, message: string, lead_id: string, case_id: string, case: Object}>}
 */
export const convertLeadToCase = async (leadId, caseData = {}) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/convert`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(caseData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to convert lead to case");
  }

  return response.json();
};

/**
 * Fetch leads from the marketplace pool (preview only, no PII)
 * @param {Object} filters - Optional filters
 * @param {string} filters.source - Filter by source (e.g., "rapidrates", "nhamy")
 * @param {string} filters.property_state - Filter by state
 * @param {string} filters.property_type - Filter by property type
 * @param {number} filters.min_amount - Minimum loan amount
 * @param {number} filters.max_amount - Maximum loan amount
 * @param {number} filters.limit - Max results to return
 * @returns {Promise<{leads: LeadPreview[], total_count: number}>}
 */
export const fetchLeadPool = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.source) params.append("source", filters.source);
  if (filters.property_state) params.append("property_state", filters.property_state);
  if (filters.property_type) params.append("property_type", filters.property_type);
  if (filters.min_amount) params.append("min_amount", filters.min_amount.toString());
  if (filters.max_amount) params.append("max_amount", filters.max_amount.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/leads/pool${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch lead pool");
  }

  return response.json();
};

/**
 * Unlock a lead from the marketplace (requires access code + credits)
 * @param {string} leadId - The lead ID to unlock
 * @param {string} accessCode - The 8-character access code from email
 * @returns {Promise<{success: boolean, lead: Lead, credits_charged: number}>}
 */
export const unlockLead = async (leadId, accessCode) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/unlock`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ access_code: accessCode }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 400) {
      throw new Error(errorData.detail || "Invalid or expired access code");
    }
    if (response.status === 402) {
      throw new Error(errorData.detail || "Insufficient credits to unlock this lead");
    }
    if (response.status === 409) {
      throw new Error(errorData.detail || "You have already unlocked this lead");
    }
    throw new Error(errorData.detail || "Failed to unlock lead");
  }

  return response.json();
};

/**
 * Format lead date for display
 * @param {string} dateStr - ISO timestamp
 * @returns {string}
 */
export const formatLeadDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined
  });
};

/**
 * Format phone number for display
 * @param {string} phone
 * @returns {string}
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Format currency for display
 * @param {string|number} value
 * @returns {string}
 */
export const formatCurrency = (value) => {
  if (!value) return "";
  const num = typeof value === "string" ? parseFloat(value.replace(/[^0-9.-]/g, "")) : value;
  if (isNaN(num)) return value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// ==================== ARCHIVE FUNCTIONS ====================

/**
 * Fetch archived leads
 * @param {Object} options - Query options
 * @param {boolean} options.include_team - Include team members' archived leads
 * @returns {Promise<{leads: Lead[], total_count: number}>}
 */
export const fetchArchivedLeads = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.include_team) params.append("include_team", "true");

  const url = `${BASE_URL}/api/leads/archived${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetchWithAuth(url, {
    headers: { accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch archived leads");
  }

  return response.json();
};

/**
 * Archive a lead
 * @param {string} leadId - The lead ID to archive
 * @param {string} reason - Optional reason for archiving (max 500 chars)
 * @returns {Promise<{success: boolean, message: string, lead_id: string}>}
 */
export const archiveLead = async (leadId, reason = "") => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/archive`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ reason }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404) {
      throw new Error("Lead not found or you don't have access to it");
    }
    if (response.status === 400) {
      throw new Error(errorData.detail || "Lead is already archived");
    }
    throw new Error(errorData.detail || "Failed to archive lead");
  }

  return response.json();
};

/**
 * Restore an archived lead
 * @param {string} leadId - The lead ID to restore
 * @returns {Promise<{success: boolean, message: string, lead_id: string}>}
 */
export const restoreLead = async (leadId) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/${leadId}/restore`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404) {
      throw new Error("Lead not found or you don't have access to it");
    }
    if (response.status === 400) {
      throw new Error(errorData.detail || "Lead is not archived");
    }
    throw new Error(errorData.detail || "Failed to restore lead");
  }

  return response.json();
};

// Common archive reasons for UI dropdown
export const ARCHIVE_REASONS = [
  "Not qualified",
  "Not interested",
  "Wrong number",
  "Duplicate lead",
  "Unable to contact",
  "Went with another lender",
  "Other",
];

// ==================== TEAM CONTEXT ====================

/**
 * Fetch the team context for the current user
 * Returns the team owner ID which should be used for reports
 * @returns {Promise<{success: boolean, data: {team_owner_id: string, is_owner: boolean, user_email: string}}>}
 */
export const fetchTeamContext = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/team-context`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch team context");
  }

  return response.json();
};

// ==================== DAILY REPORTS ====================
// Note: The API automatically resolves the team based on the authenticated user.
// Team owners see their team's reports, team members see their team owner's reports,
// and solo users see their own reports. No team_id parameter is needed.

/**
 * Fetch list of available daily reports
 * The API automatically resolves the team based on the authenticated user.
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of reports to fetch (1-90, default 30)
 * @returns {Promise<{success: boolean, data: {team_id: string, reports: Array, total_count: number}}>}
 */
export const fetchLeadsReports = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append("limit", options.limit.toString());

  const queryString = params.toString();
  const url = `${BASE_URL}/api/leads-reports${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch leads reports");
  }

  return response.json();
};

/**
 * Fetch a specific daily report by date
 * The API automatically resolves the team based on the authenticated user.
 * @param {string} reportDate - Date in YYYY-MM-DD format
 * @returns {Promise<{success: boolean, data: Object}>}
 */
export const fetchLeadsReport = async (reportDate) => {
  const url = `${BASE_URL}/api/leads-reports/${reportDate}`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Report not found for this date");
    }
    if (response.status === 400) {
      throw new Error("Invalid date format");
    }
    throw new Error("Failed to fetch leads report");
  }

  return response.json();
};

/**
 * Fetch the latest daily report
 * The API automatically resolves the team based on the authenticated user.
 * @returns {Promise<{success: boolean, data: Object}>}
 */
export const fetchLatestLeadsReport = async () => {
  const url = `${BASE_URL}/api/leads-reports/latest`;

  const response = await fetchWithAuth(url, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("No reports available");
    }
    throw new Error("Failed to fetch latest leads report");
  }

  return response.json();
};

/**
 * Generate a new daily report
 * The API automatically resolves the team based on the authenticated user.
 * @param {boolean} force - If true, force regeneration even if report exists for today (costs 500 credits)
 * @returns {Promise<{success: boolean, data: Object, message: string}>}
 */
export const generateLeadsReport = async (force = false) => {
  const url = `${BASE_URL}/api/leads-reports/generate?force=${force}`;

  const response = await fetchWithAuth(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || "Report already exists for today. Use force=true to regenerate.");
    }
    if (response.status === 402) {
      throw new Error("Insufficient credits to generate report");
    }
    throw new Error("Failed to generate leads report");
  }

  return response.json();
};

// ==================== MARKETPLACE ACTIVITY / REPORTS ====================

/**
 * Fetch marketplace activity statistics
 * Returns lead counts, pipeline values, source breakdown, and state breakdown
 * @returns {Promise<{
 *   summary: {today: {...}, this_week: {...}, this_month: {...}, all_time: {...}},
 *   by_source: Array<{source: string, count: number, pipeline_value: number}>,
 *   by_state: Array<{state: string, count: number, pipeline_value: number}>
 * }>}
 */
export const fetchMarketplaceActivity = async () => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/marketplace/activity`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch marketplace activity");
  }

  return response.json();
};

/**
 * Format currency in abbreviated form (e.g., $93.1M, $398K)
 * @param {number} value
 * @returns {string}
 */
export const formatCurrencyAbbreviated = (value) => {
  if (!value || value === 0) return "$0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1000000000) {
    return `${sign}$${(absValue / 1000000000).toFixed(1)}B`;
  }
  if (absValue >= 1000000) {
    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}$${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}$${absValue.toFixed(0)}`;
};

/**
 * Format percentage with + sign for positive values
 * @param {number} value
 * @returns {string}
 */
export const formatGrowthPercent = (value) => {
  if (value === null || value === undefined) return "";
  const formatted = Math.abs(value).toFixed(0);
  if (value > 0) return `+${formatted}%`;
  if (value < 0) return `-${formatted}%`;
  return "0%";
};

/**
 * Send lead information to realtor(s)
 * Ultra/Enterprise tier only
 * @param {Object} data - Lead and realtor information
 * @param {Array} data.realtors - Array of {email, name} objects (max 20)
 * @param {string} data.buyer_name - Buyer's name
 * @param {string} data.buyer_email - Buyer's email
 * @param {string} data.buyer_phone - Buyer's phone (10-20 chars)
 * @param {string} data.loan_purpose - e.g., "Purchase", "Refinance"
 * @param {string} data.property_type - e.g., "Single Family", "Condo"
 * @param {string} data.property_location - e.g., "Houston, TX"
 * @param {string} data.price_range - e.g., "$300,000 - $400,000"
 * @param {string} data.timeline - e.g., "Within 3 months", "ASAP"
 * @param {string} [data.additional_notes] - Optional notes (max 1000 chars)
 * @returns {Promise<Object>} Response with success status and results per realtor
 */
export const sendLeadToRealtor = async (data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/send-to-realtor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(errorData.message || "This feature is only available for Ultra and Enterprise plans");
    }
    throw new Error(errorData.message || "Failed to send lead to realtor(s)");
  }

  return response.json();
};

// Loan purpose options for Send to Realtor
export const LOAN_PURPOSE_OPTIONS = [
  "Purchase",
  "Refinance",
  "Cash-Out Refinance",
  "Home Equity",
  "Construction",
];

// Property type options for Send to Realtor
export const PROPERTY_TYPE_OPTIONS = [
  "Single Family",
  "Condo",
  "Townhouse",
  "Multi-Family (2-4 units)",
  "Investment Property",
  "Manufactured Home",
];

// Timeline options for Send to Realtor
export const TIMELINE_OPTIONS = [
  "ASAP",
  "Within 1 month",
  "Within 3 months",
  "Within 6 months",
  "Just exploring",
];

/**
 * Send lead information to insurance agent(s)
 * @param {Object} data - Lead and insurance agent information
 * @param {Array} data.agents - Array of {email, name} objects (max 20)
 * @param {string} data.buyer_name - Buyer's name
 * @param {string} data.buyer_email - Buyer's email
 * @param {string} data.buyer_phone - Buyer's phone (10-20 chars)
 * @param {string} data.loan_purpose - e.g., "Purchase", "Refinance"
 * @param {string} data.property_type - e.g., "Single Family", "Condo"
 * @param {string} data.property_location - e.g., "Houston, TX"
 * @param {string} data.price_range - e.g., "$300,000 - $400,000"
 * @param {string} data.timeline - e.g., "Within 3 months", "ASAP"
 * @param {string} [data.additional_notes] - Optional notes (max 1000 chars)
 * @returns {Promise<Object>} Response with success status and results per agent
 */
export const sendLeadToInsurance = async (data) => {
  const response = await fetchWithAuth(`${BASE_URL}/api/leads/send-to-insurance`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 403) {
      throw new Error(errorData.message || "This feature is only available for Ultra and Enterprise plans");
    }
    throw new Error(errorData.message || "Failed to send lead to insurance agent(s)");
  }

  return response.json();
};
